import { Stack } from '@mui/material';
import { z } from 'zod';

import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockTextField } from '../../../ConfigFields/TextField';
import { type BlockConfigProps } from '../../../types';

const GS_PATHS_HELPER_TEXT = `Provide templatized text for a list of cloud storage paths to the document files, e.g. {{ myBlock.data.fieldThatHasAnArrayOfGsPaths  }}.`;
const FILE_REFERENCE_HELPER_TEXT = `A string in the format {type}:{identifier}.`;

function FormFields() {
  return (
    <Stack spacing={2}>
      <BlockTextField
        fieldName="config.gsPaths"
        label="GS Paths"
        required
        helperText={GS_PATHS_HELPER_TEXT}
      />
      <BlockValueTemplateField
        fieldName="config.fileReference"
        label="File Reference"
        helperText={FILE_REFERENCE_HELPER_TEXT}
      />
    </Stack>
  );
}

const documentationMarkdown = `
# OCR Block

Extracts text from images and documents stored in Secure Notable Cloud

## Input Formats

The OCR block accepts the following formats for the "gsPaths" field:

### Single Path (String)
\`\`\`
gs://bucket/folder/test_image.png
\`\`\`

### Array of Paths (String Array)
\`\`\`
[
  "gs://bucket/folder/image1.png",
  "gs://bucket/folder/image2.png"
]
\`\`\`

### Legacy Format (Object Array)
\`\`\`
[
  { "gsPath": "gs://bucket/folder/image1.png" },
  { "gsPath": "gs://bucket/folder/image2.png" }
]
\`\`\`

## Dynamic Paths


You can use template variables to dynamically set paths:
- Single value: \`{{ previousBlock.data.imagePath }}\`
- Array value: \`{{ previousBlock.data.imagePaths }}\`

## File references

The OCR block can accept a "file reference" which is a specially formatted
string that represents a file known to notable. These are in the format
"{type}:{identifier}". For example: "externalDocumentPacket:<<uuid>>"

## Output

Returns an array of \`DocumentTextDetectionResults\` containing the extracted text and metadata for each processed file.

## Error Handling
- Throws an error if any file fails OCR processing
- Invalid paths or inaccessible files will cause block execution to fail
`;

export const ocrConfig: BlockConfigProps = {
  nodeHandles: [{ type: 'target' }, { type: 'source' }],
  defaultLabelPrefix: 'OCR',
  defaultConfig: {
    gsPaths: [],
  },
  configFields: {
    schema: z.object({
      gsPaths: z.string(),
      fileReference: z.string().optional(),
    }),
    FormFields,
  },
  documentationMarkdown,
};
